import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { PackagingSvhcAuditRunResults } from './PackagingSvhcAuditRunResults'
import {
  type PackagingSvhcAuditResult,
  useRunPackagingItemSvhcAudit,
} from '@/shared/hooks/api/packagingSuppliers'

type PackagingItemSvhcAuditRunSheetProps = {
  supplierId: string
  itemId: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  latestEntryIsManualOverride?: boolean
}

export function PackagingItemSvhcAuditRunSheet({
  supplierId,
  itemId,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  latestEntryIsManualOverride = false,
}: PackagingItemSvhcAuditRunSheetProps) {
  const { t } = useTranslation(['packagingSuppliers', 'common'])
  const [internalOpen, setInternalOpen] = useState(false)
  const [replaceOverrideConfirmOpen, setReplaceOverrideConfirmOpen] =
    useState(false)
  const [latestResult, setLatestResult] =
    useState<PackagingSvhcAuditResult | null>(null)
  const svhcAuditMutation = useRunPackagingItemSvhcAudit(supplierId, itemId)

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const setOpen = (nextOpen: boolean) => {
    if (isControlled) {
      controlledOnOpenChange?.(nextOpen)
    } else {
      setInternalOpen(nextOpen)
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setLatestResult(null)
      svhcAuditMutation.reset()
    }
  }

  const handleRun = async () => {
    setLatestResult(null)
    try {
      const nextResult = await svhcAuditMutation.mutateAsync()
      setLatestResult(nextResult)
    } catch {
      // isError state drives the sheet error message.
    }
  }

  const handleFooterClick = () => {
    if (latestResult) {
      handleOpenChange(false)
      return
    }
    if (latestEntryIsManualOverride) {
      setReplaceOverrideConfirmOpen(true)
      return
    }
    void handleRun()
  }

  let footerButtonLabel = t('itemDetail.svhcAudit.run')
  if (svhcAuditMutation.isPending) {
    footerButtonLabel = t('itemDetail.svhcAudit.running')
  } else if (latestResult) {
    footerButtonLabel = t('common:actions.close')
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent className='flex flex-col sm:max-w-lg'>
          <SheetHeader>
            <SheetTitle>{t('itemDetail.svhcAudit.runSheetTitle')}</SheetTitle>
            <SheetDescription>
              {t('itemDetail.svhcAudit.runSheetDescription')}
            </SheetDescription>
          </SheetHeader>
          <div className='min-h-0 flex-1 space-y-4 overflow-y-auto py-4'>
            {svhcAuditMutation.isError ? (
              <p className='text-sm text-destructive'>
                {t('itemDetail.svhcAudit.runFailed')}
              </p>
            ) : null}
            {latestResult ? (
              <PackagingSvhcAuditRunResults result={latestResult} />
            ) : null}
          </div>
          <SheetFooter>
            <Button
              type='button'
              onClick={handleFooterClick}
              disabled={svhcAuditMutation.isPending}
            >
              {footerButtonLabel}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={replaceOverrideConfirmOpen}
        onOpenChange={setReplaceOverrideConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('itemDetail.manualOverride.replaceOverrideTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('itemDetail.manualOverride.replaceOverrideBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('itemDetail.manualOverride.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                setReplaceOverrideConfirmOpen(false)
                void handleRun()
              }}
            >
              {t('itemDetail.svhcAudit.run')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
